import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useSvgToolHandoffReceiver } from "../../hooks/useSvgToolHandoffReceiver";
import { useUndoRedo } from "../../hooks/useUndoRedo";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import { cloneArray } from "../../utils/undoSnapshots";
import {
    extractViewBox,
    isValidSvg,
    getSvgDimensions,
    extractShapeElements,
    applyDistortionToShapes,
} from "../../utils/svgUtils";

interface UploadedSvg {
    name: string;
    content: string;
}

export type CurveShape = "linear" | "exponential" | "ease-in-out" | "quadratic";
export type DistortionMode = "randomize" | "repel" | "attract";

export interface DistortionPoint {
    id: string;
    x: number;
    y: number;
    intensity: number;
    size: number;
    curveShape: CurveShape;
    mode: DistortionMode;
}

interface PointDistortionState {
    svg: UploadedSvg | null;
    points: DistortionPoint[];
    error: string | null;
    setSvg: (svg: UploadedSvg | null) => void;
    setError: (e: string | null) => void;
    addPoint: (x: number, y: number) => void;
    removePoint: (id: string) => void;
    updatePoint: (id: string, updates: Partial<DistortionPoint>) => void;
    svgInputRef: React.RefObject<HTMLInputElement>;
    handleFileLoad: (event: React.ChangeEvent<HTMLInputElement>) => void;
    viewBoxAttr: string;
    distortedSvgContent: string | null;
    canExport: boolean;
    buildExportSvgString: () => string;
    handleDownload: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
    undo: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
}

const PointDistortionContext = createContext<PointDistortionState | null>(null);

export function usePointDistortion() {
    const context = useContext(PointDistortionContext);
    if (!context) {
        throw new Error(
            "usePointDistortion must be used within PointDistortionProvider"
        );
    }
    return context;
}

interface PointDistortionProviderProps {
    children: React.ReactNode;
}

export function PointDistortionProvider({
    children,
}: PointDistortionProviderProps) {
    const [svg, setSvg] = useState<UploadedSvg | null>(null);
    const [points, setPoints] = useState<DistortionPoint[]>([]);
    const [error, setError] = useState<string | null>(null);

    const svgInputRef = useRef<HTMLInputElement | null>(null);
    const pointsRef = useRef(points);
    pointsRef.current = points;
    const history = useUndoRedo<DistortionPoint[]>({ clone: cloneArray });

    const pushPointsSnapshot = useCallback(() => {
        history.push(pointsRef.current);
    }, [history]);

    const undo = useCallback(() => {
        history.undo(pointsRef.current, setPoints);
    }, [history]);

    const redo = useCallback(() => {
        history.redo(pointsRef.current, setPoints);
    }, [history]);

    const handleFileLoad = (
        event: React.ChangeEvent<HTMLInputElement>
    ): void => {
        const file = event.target.files?.[0];
        if (!file) return;

        setError(null);

        if (
            !file.type.includes("svg") &&
            !file.name.toLowerCase().endsWith(".svg")
        ) {
            setError("Please choose an SVG file.");
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const text = typeof reader.result === "string" ? reader.result : "";

            if (!text) {
                setError("Unable to read SVG file.");
                return;
            }

            if (!isValidSvg(text)) {
                setError("Selected file does not appear to be valid SVG.");
                return;
            }

            setSvg({
                name: file.name,
                content: text,
            });
            history.clear();
            setPoints([]);
        };

        reader.onerror = () => {
            setError("Error reading file.");
        };

        reader.readAsText(file);
    };

    const viewBoxAttr = useMemo(() => {
        if (!svg) return "0 0 200 200";
        return extractViewBox(svg.content)?.viewBox ?? "0 0 200 200";
    }, [svg]);

    const svgDimensions = useMemo(() => {
        if (!svg) return null;
        return getSvgDimensions(svg.content);
    }, [svg]);

    const distortedSvgContent = useMemo(() => {
        if (!svg || points.length === 0 || !svgDimensions) return null;

        const shapeElements = extractShapeElements(svg.content);
        if (shapeElements.length === 0) {
            console.warn("No shape elements found in SVG");
            return null;
        }

        // Convert percentage-based sizes to SVG units
        const svgDiagonal = Math.sqrt(
            svgDimensions.width * svgDimensions.width +
                svgDimensions.height * svgDimensions.height
        );
        const pointsWithAbsoluteSize = points.map((point) => ({
            ...point,
            size: (point.size / 100) * svgDiagonal,
        }));

        const distortedShapes = applyDistortionToShapes(
            shapeElements,
            pointsWithAbsoluteSize
        );

        // Rebuild SVG with distorted shapes by replacing in order
        // Sort by startIndex descending to replace from end to start (preserves indices)
        const sortedShapes = [...shapeElements].sort(
            (a, b) => b.startIndex - a.startIndex
        );

        let result = svg.content;
        for (let i = 0; i < sortedShapes.length; i++) {
            const shape = sortedShapes[i];
            const shapeIndex = shapeElements.indexOf(shape);
            const distorted = distortedShapes[shapeIndex].element;

            result =
                result.slice(0, shape.startIndex) +
                distorted +
                result.slice(shape.endIndex);
        }

        return result;
    }, [svg, points]);

    const canExport = !!svg && points.length > 0 && !!distortedSvgContent;

    const buildExportSvgString = () => {
        if (!distortedSvgContent) return "";
        return distortedSvgContent;
    };

    const handleDownload = () => {
        if (!canExport) return;
        const svgString = buildExportSvgString();
        if (!svgString) return;

        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `${svg?.name || "distorted"}-${Date.now()}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const addPoint = (x: number, y: number) => {
        pushPointsSnapshot();
        const newPoint: DistortionPoint = {
            id: `point-${Date.now()}-${Math.random()}`,
            x,
            y,
            intensity: 10,
            size: 50, // Percentage (0-100)
            curveShape: "linear",
            mode: "randomize",
        };
        setPoints((prev) => [...prev, newPoint]);
    };

    const removePoint = (id: string) => {
        pushPointsSnapshot();
        setPoints((prev) => prev.filter((p) => p.id !== id));
    };

    const updatePoint = (id: string, updates: Partial<DistortionPoint>) => {
        pushPointsSnapshot();
        setPoints((prev) =>
            prev.map((p) => (p.id === id ? { ...p, ...updates } : p))
        );
    };

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({ points }),
        [points],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.points != null) setPoints(values.points as DistortionPoint[]);
    }, []);

    const { resetParameters: resetStoredParameters } = useToolParamPersistence({
        toolId: "point-distortion",
        defaultPreset: { points: [] as DistortionPoint[] },
        getPresetValues,
        applyPreset,
    });

    const resetParameters = useCallback(() => {
        history.clear();
        resetStoredParameters();
    }, [history, resetStoredParameters]);

    useSvgToolHandoffReceiver(
        "point-distortion",
        {
            onPrimary: (svg) => {
                setError(null);
                setSvg(svg);
                history.clear();
                setPoints([]);
            },
        },
        { setError },
    );

    const value: PointDistortionState = {
        svg,
        points,
        error,
        setSvg,
        setError,
        addPoint,
        removePoint,
        updatePoint,
        svgInputRef,
        handleFileLoad,
        viewBoxAttr,
        distortedSvgContent,
        canExport,
        buildExportSvgString,
        handleDownload,
        applyPreset,
        getPresetValues,
        resetParameters,
        undo,
        redo,
        canUndo: history.canUndo,
        canRedo: history.canRedo,
    };

    return (
        <PointDistortionContext.Provider value={value}>
            {children}
        </PointDistortionContext.Provider>
    );
}
